import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faReceipt } from "@fortawesome/free-solid-svg-icons";
import { Badge, Button, Card, Col, Row } from "@themesberg/react-bootstrap";
import ContentLoader from "react-content-loader";
import { useHistory, useParams } from "react-router-dom";

import { getVendorOrderById } from "../api/vendor.api";
import { useToast } from "../components/ToastProvider";
import { Routes } from "../routes";
import { resolveAssetUrl } from "../utils/assetUrl";

const formatDateTime = (dateValue) => {
  if (!dateValue) return "N/A";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString();
};

const STATUS_VARIANTS = {
  pending: { bg: "secondary", text: "Pending" },
  completed: { bg: "warning", text: "Completed" },
  acknowledged: { bg: "success", text: "Acknowledged" },
  rejected: { bg: "danger", text: "Rejected" },
  cancelled: { bg: "dark", text: "Cancelled" }
};

const getStatusBadge = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();
  const config = STATUS_VARIANTS[normalizedStatus] || { bg: "light", text: status || "N/A" };

  return (
    <Badge bg={config.bg} className="px-3 py-2 text-uppercase small">
      {config.text}
    </Badge>
  );
};

const OrderDetailsSkeleton = () => (
  <div>
    <Card border="light" className="shadow-sm mb-4">
      <Card.Body>
        <ContentLoader speed={2} viewBox="0 0 400 120" backgroundColor="#f3f3f3" foregroundColor="#e0e0e0">
          <rect x="0" y="0" width="200" height="20" rx="4" />
          <rect x="0" y="40" width="120" height="14" rx="4" />
          <rect x="0" y="65" width="80" height="12" rx="4" />
          <rect x="200" y="40" width="180" height="14" rx="4" />
          <rect x="200" y="65" width="150" height="12" rx="4" />
        </ContentLoader>
      </Card.Body>
    </Card>
    {[1, 2].map((i) => (
      <Card key={i} border="light" className="shadow-sm mb-3">
        <Card.Body>
          <ContentLoader speed={2} viewBox="0 0 400 100" backgroundColor="#f3f3f3" foregroundColor="#e0e0e0">
            <rect x="0" y="10" width="84" height="84" rx="8" />
            <rect x="100" y="20" width="150" height="14" rx="4" />
            <rect x="100" y="45" width="120" height="12" rx="4" />
            <rect x="100" y="65" width="80" height="10" rx="4" />
            <rect x="300" y="25" width="60" height="14" rx="4" />
            <rect x="300" y="50" width="40" height="12" rx="4" />
          </ContentLoader>
        </Card.Body>
      </Card>
    ))}
  </div>
);

export default () => {
  const history = useHistory();
  const { id } = useParams();
  const { showToast } = useToast();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrder = async () => {
      setLoading(true);

      try {
        const response = await getVendorOrderById(id);
        console.log("Fetched order details:", response);
        setOrder(response);
      } catch (error) {
        showToast({ message: error.message || "Failed to load order details", variant: "danger" });
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [id, showToast]);

  return (
    <div className="py-4">
      <div className="px-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="mb-0">Order Details</h3>
          </div>
          <Button variant="outline-secondary" onClick={() => history.push(Routes.MyOrders.path)} size="sm">
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
            Back
          </Button>
        </div>
        <p className="text-muted mb-4 small">Review the full contents of your order.</p>
      </div>
      {loading ? (
        <OrderDetailsSkeleton />
      ) : !order ? null : (
        <>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Body>
              <div className="d-flex align-items-center mb-3">
                <FontAwesomeIcon icon={faReceipt} className="me-2 text-primary" />
                <h5 className="mb-0">Order #{order.id}</h5>
              </div>
              <Row>
                <Col xs={12} md={6} className="mb-3">
                  <div className="small text-muted mb-1">Status</div>
                  <div>{getStatusBadge(order.status)}</div>
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <div className="small text-muted mb-1">Created At</div>
                  <div className="fw-semibold">{formatDateTime(order.created_at)}</div>
                </Col>
                {order.total_amount && (
                  <Col xs={12} className="mb-3">
                    <div className="bg-light rounded p-3 text-center">
                      <div className="small text-muted mb-1">Total Amount</div>
                      <div className="fw-bold text-primary" style={{ fontSize: '1.5rem' }}>${Number(order.total_amount).toFixed(2)}</div>
                    </div>
                  </Col>
                )}
                <Col xs={12} md={12} className="mt-3">
                  {order.reject_reason && (
                    <>
                      <div className="small text-muted mb-1">Reject Reason</div>
                      <div className="fw-semibold">{order.reject_reason}</div>
                    </>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row>
            {Array.isArray(order.items) && order.items.length > 0 ? order.items.map((item) => (
              <Col xs={12} key={item.id} className="mb-3">
                <Card border="light" className="shadow-sm">
                  <Card.Body>
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                      {item.image_url ? (
                        <img
                          src={resolveAssetUrl(item.image_url)}
                          alt={item.variant_name}
                          style={{ width: "84px", height: "84px", objectFit: "cover", borderRadius: "1rem" }}
                        />
                      ) : (
                        <div className="border rounded d-flex align-items-center justify-content-center text-muted" style={{ width: "84px", height: "84px" }}>
                          N/A
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <div className="small text-muted">{item.product_name}</div>
                        <h6 className="mb-1">{item.variant_name}</h6>
                        <div className="small text-muted">SKU: {item.sku}</div>
                      </div>
                      <div className="text-md-end">
                        <div className="small text-muted">Quantity</div>
                        <div className="fw-semibold">{item.quantity}</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            )) : (
              <Col xs={12}>
                <Card border="light" className="shadow-sm">
                  <Card.Body className="text-muted">No order items found.</Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        </>
      )}
    </div>
  );
};
